import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  Sparkles, Building2, Hand, MessageSquare, ClipboardList, Layers, Hammer,
  ClipboardCheck, ShieldCheck, Frame, Gem, Store, Droplet, RefreshCw, Boxes,
  Ruler, Cable, Clock, Star, ArrowRight, ArrowLeft, ChevronRight,
} from "lucide-react";
import { Navbar } from "@/components/site/Navbar";

const WHY = [
  { icon: Sparkles, title: "Completely Custom Designs", body: "Every aquarium is designed specifically for your space, style, and vision—ensuring a one-of-a-kind centerpiece that enhances its surroundings." },
  { icon: Building2, title: "Residential & Commercial Expertise", body: "From luxury homes and executive offices to restaurants, hotels, healthcare facilities, and corporate spaces, Seatech delivers professional installations for every environment." },
  { icon: Hand, title: "White-Glove Installation", body: "Our experienced team carefully installs every component, thoroughly tests the system, and leaves your space cleaner than when we arrived." },
];

const STEPS = [
  { icon: MessageSquare, title: "Consultation", body: "Meet with our design experts to discuss your vision, available space, lifestyle, and aquarium goals." },
  { icon: ClipboardList, title: "Custom Design", body: "Develop a personalized aquarium solution considering dimensions, viewing angles, cabinetry, filtration, lighting, and overall aesthetics." },
  { icon: Layers, title: "Material Selection", body: "Choose premium acrylic or glass options, cabinetry finishes, aquascaping materials, and supporting equipment." },
  { icon: Hammer, title: "Professional Fabrication", body: "Every custom aquarium is expertly built using premium materials and industry-leading craftsmanship." },
  { icon: Hand, title: "White-Glove Installation", body: "Our team carefully installs your aquarium, equipment, plumbing, lighting, and life-support systems with meticulous attention to detail." },
  { icon: ClipboardCheck, title: "System Testing", body: "Every component is thoroughly tested to ensure optimal performance before introducing aquatic life." },
  { icon: ShieldCheck, title: "Ongoing Support", body: "Professional maintenance programs help protect your investment while keeping your aquarium healthy and beautiful for years to come." },
];

const SOLUTIONS = [
  { icon: Frame, title: "Built-In Wall Aquariums", body: "Create dramatic living displays seamlessly integrated into your architecture." },
  { icon: Gem, title: "Freestanding Showpiece Aquariums", body: "Luxury centerpiece aquariums designed to become the focal point of any room." },
  { icon: Store, title: "Commercial Installations", body: "Professional aquarium solutions for offices, restaurants, medical practices, hotels, retail spaces, and corporate environments." },
  { icon: Droplet, title: "Acrylic Aquariums", body: "Premium custom acrylic aquariums offering exceptional clarity, strength, and design flexibility for virtually any application." },
  { icon: RefreshCw, title: "Aquarium Redesigns", body: "Transform outdated aquariums with updated layouts, modern equipment, and refreshed aesthetics." },
  { icon: Boxes, title: "Custom Cabinetry & Stands", body: "Beautifully crafted cabinetry designed to complement your interior while safely supporting your aquarium system." },
];

const WHITEGLOVE = [
  { title: "Precision Installation", body: "Every aquarium is carefully positioned, leveled, connected, and tested to ensure flawless operation." },
  { title: "Respect For Your Space", body: "Protective floor coverings, organized work areas, and meticulous cleanup ensure your home or office looks even better after installation." },
  { title: "Every Detail Matters", body: "From plumbing concealment to equipment organization and final polishing, every installation reflects Seatech's commitment to excellence." },
];

const INVEST = [
  { icon: Ruler, title: "Custom Fit", body: "Every aquarium is engineered specifically for your environment." },
  { icon: Cable, title: "Professional Equipment Integration", body: "Filtration, lighting, plumbing, and electrical systems are seamlessly incorporated into the overall design." },
  { icon: Clock, title: "Long-Term Reliability", body: "Proper planning and installation help maximize performance while minimizing future maintenance concerns." },
  { icon: Star, title: "Stunning Visual Impact", body: "A professionally designed aquarium becomes a signature architectural feature that elevates the entire space." },
];

const GALLERY = [
  "/media/gallery/seatech-1.jpg",
  "/media/gallery/seatech-2.jpg",
  "/media/gallery/seatech-3.jpg",
  "/media/gallery/seatech-4.jpg",
  "/media/gallery/seatech-5.jpg",
  "/media/gallery/seatech-6.jpg",
  "/media/gallery/seatech-7.jpg",
  "/media/gallery/seatech-8.jpg",
  "/media/gallery/seatech-9.jpg",
];

const ACRYLIC_IMG = "https://images.unsplash.com/photo-1535591273668-578e31182c4f?q=85&w=1200";
const BEFORE_IMG = "https://images.unsplash.com/photo-1650200333906-741e9317799f?crop=entropy&cs=srgb&fm=jpg&q=80&w=1000";
const AFTER_IMG = "https://images.unsplash.com/photo-1764175760241-29adb8c13e48?crop=entropy&cs=srgb&fm=jpg&q=80&w=1000";

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 44, filter: "blur(6px)" }}
    whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

const Particles = () => {
  const parts = useMemo(
    () => Array.from({ length: 16 }).map((_, i) => ({
      id: i, left: Math.random() * 100, size: 4 + Math.random() * 16,
      duration: 12 + Math.random() * 12, delay: Math.random() * 12,
    })),
    []
  );
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      {parts.map((p) => (
        <motion.span
          key={p.id}
          initial={{ y: "110%", opacity: 0 }}
          animate={{ y: "-15%", opacity: [0, 0.5, 0.5, 0] }}
          transition={{ duration: p.duration, delay: p.delay, repeat: Infinity, ease: "linear" }}
          className="absolute rounded-full border border-[#9fb2c4]/25 bg-[#cfe0ec]/10 backdrop-blur-[1px]"
          style={{ left: `${p.left}%`, width: p.size, height: p.size }}
        />
      ))}
    </div>
  );
};

const LightRays = () => (
  <div className="pointer-events-none fixed inset-0 overflow-hidden">
    {[14, 46, 76].map((left, i) => (
      <motion.div
        key={i}
        animate={{ opacity: [0.04, 0.13, 0.04] }}
        transition={{ duration: 10 + i * 2, repeat: Infinity, ease: "easeInOut" }}
        className="absolute -top-1/3 h-[160%] w-44 rotate-12 bg-gradient-to-b from-[#dfe9f2]/30 via-[#35e0d1]/10 to-transparent blur-2xl"
        style={{ left: `${left}%` }}
      />
    ))}
  </div>
);

const BeforeAfter = () => {
  const [pos, setPos] = useState(50);
  return (
    <div className="relative aspect-[4/3] w-full select-none overflow-hidden rounded-3xl border border-[#F7FAFC]/12" data-testid="acrylic-before-after">
      <img src={AFTER_IMG} alt="After acrylic restoration" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 overflow-hidden" style={{ width: `${pos}%` }}>
        <img src={BEFORE_IMG} alt="Before acrylic restoration" className="h-full w-full object-cover" style={{ width: "100%", filter: "grayscale(0.35) brightness(0.75)" }} />
        <span className="absolute left-3 top-3 rounded-full bg-[#03101d]/70 px-3 py-1 font-body text-[10px] font-semibold uppercase tracking-[0.16em] text-[#F7FAFC] backdrop-blur-md">Before</span>
      </div>
      <span className="absolute right-3 top-3 rounded-full bg-[#35e0d1]/20 px-3 py-1 font-body text-[10px] font-semibold uppercase tracking-[0.16em] text-[#7fe8ff] backdrop-blur-md">After</span>
      <div className="absolute top-0 bottom-0 w-0.5 bg-[#7fe8ff]" style={{ left: `${pos}%` }}>
        <div className="absolute top-1/2 left-1/2 flex h-9 w-9 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-[#7fe8ff]/60 bg-[#03101d]/80 text-[#7fe8ff] backdrop-blur-md">
          <ChevronRight className="h-4 w-4 -mr-1 rotate-180" />
          <ChevronRight className="h-4 w-4 -ml-1" />
        </div>
      </div>
      <input
        type="range" min="0" max="100" value={pos}
        onChange={(e) => setPos(Number(e.target.value))}
        aria-label="Compare before and after"
        data-testid="acrylic-slider"
        className="absolute inset-0 h-full w-full cursor-ew-resize opacity-0"
      />
    </div>
  );
};

export default function AquariumDesign() {
  const navigate = useNavigate();

  const goHome = (target) => {
    navigate("/", { state: { scrollTarget: target } });
  };

  return (
    <main data-testid="design-page" className="relative min-h-screen bg-[#071A29] text-[#F7FAFC]">
      {/* Fixed looping background */}
      <div className="fixed inset-0 z-0">
        <video
          className="absolute inset-0 h-full w-full object-cover"
          src="/media/reef-loop.mp4"
          autoPlay loop muted playsInline
          data-testid="design-bg-video"
        />
        <div className="absolute inset-0 bg-[#050d1a]/90" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#04101c]/94 via-[#0a1626]/86 to-[#04101c]/97" />
      </div>

      <LightRays />

      <div className="relative z-10">
        <Navbar />

        {/* HERO */}
        <section className="relative flex min-h-[58vh] items-center overflow-hidden px-6 lg:px-16 pt-52 pb-10">
          <Particles />
          <div className="relative mx-auto flex w-full max-w-4xl flex-col items-center text-center">
            <Reveal>
              <span className="inline-flex items-center gap-2 rounded-full border border-[#35e0d1]/40 bg-[#35e0d1]/10 px-4 py-2 font-body text-xs font-medium uppercase tracking-[0.28em] text-[#7fe8ff]" data-testid="design-kicker">
                Custom Luxury Aquatic Environments
              </span>
            </Reveal>
            <Reveal delay={0.1}>
              <h1 className="mt-6 font-title text-3xl font-bold leading-[1.05] tracking-tight sm:text-4xl lg:text-5xl" data-testid="design-headline">
                Custom Aquarium<br />Design &amp; Installation
              </h1>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 max-w-2xl font-body text-base font-light leading-relaxed text-[#c6d6e4] lg:text-lg">
                From concept to completion, Seatech Aquariums designs, builds, and installs stunning
                custom aquarium systems that blend exceptional craftsmanship with lasting performance
                for residential and commercial environments.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
                <button
                  onClick={() => goHome("bottom")}
                  data-testid="design-hero-cta"
                  className="group inline-flex items-center gap-3 rounded-full bg-[#35e0d1] px-8 py-4 font-body text-sm font-semibold uppercase tracking-[0.16em] text-[#04221f] transition-all duration-300 hover:shadow-[0_0_40px_-8px_#35e0d1]"
                >
                  Schedule a Free Consultation
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </button>
                <button
                  onClick={() => document.getElementById("portfolio")?.scrollIntoView({ behavior: "smooth" })}
                  data-testid="design-hero-secondary"
                  className="inline-flex items-center gap-2 rounded-full border border-[#F7FAFC]/25 px-6 py-4 font-body text-sm font-light uppercase tracking-[0.16em] text-[#F7FAFC]/90 backdrop-blur-md transition-all duration-300 hover:border-[#F7FAFC]/60"
                >
                  View Our Portfolio
                </button>
              </div>
            </Reveal>
          </div>
        </section>

        {/* WHY CHOOSE SEATECH */}
        <section className="px-6 lg:px-16 py-24">
          <div className="mx-auto max-w-6xl">
            <Reveal><h2 className="font-title text-3xl font-bold tracking-tight lg:text-4xl">Why Choose Seatech</h2></Reveal>
            <div className="mt-12 grid gap-6 md:grid-cols-3">
              {WHY.map((c, i) => (
                <Reveal key={c.title} delay={i * 0.12}>
                  <div className="group h-full rounded-3xl border border-[#F7FAFC]/12 bg-[#0b1626]/50 p-8 backdrop-blur-2xl transition-all duration-500 hover:-translate-y-1.5 hover:border-[#35e0d1]/45" data-testid={`design-why-card-${i}`}>
                    <div className="flex h-14 w-14 items-center justify-center rounded-xl border border-[#35e0d1]/25 bg-[#35e0d1]/10 text-[#7fe8ff] transition-all duration-500 group-hover:shadow-[0_0_30px_-6px_#35e0d1]">
                      <c.icon className="h-7 w-7" strokeWidth={1.5} />
                    </div>
                    <h3 className="mt-6 font-title text-xl font-bold tracking-tight">{c.title}</h3>
                    <p className="mt-3 font-body text-sm font-light leading-relaxed text-[#c6d6e4]">{c.body}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* THE DESIGN PROCESS */}
        <section className="px-6 lg:px-16 py-24">
          <div className="mx-auto max-w-5xl">
            <Reveal>
              <span className="font-body text-xs font-medium uppercase tracking-[0.28em] text-[#7fe8ff]">From Concept to Completion</span>
              <h2 className="mt-3 font-title text-3xl font-bold tracking-tight lg:text-4xl">The Design Process</h2>
            </Reveal>
            <div className="relative mt-14">
              <div className="absolute left-7 top-0 bottom-0 hidden w-px bg-gradient-to-b from-[#35e0d1]/50 via-[#F7FAFC]/15 to-transparent md:block" />
              <div className="space-y-6">
                {STEPS.map((s, i) => (
                  <Reveal key={s.title} delay={i * 0.05}>
                    <div className="group relative flex items-start gap-6 rounded-3xl border border-[#F7FAFC]/12 bg-[#0b1626]/50 p-6 backdrop-blur-2xl transition-all duration-500 hover:border-[#35e0d1]/45 md:pl-8" data-testid={`design-step-${i}`}>
                      <div className="relative flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-[#35e0d1]/30 bg-[#35e0d1]/10 text-[#7fe8ff] transition-all duration-500 group-hover:shadow-[0_0_30px_-6px_#35e0d1]">
                        <s.icon className="h-6 w-6" strokeWidth={1.5} />
                      </div>
                      <div>
                        <span className="font-body text-xs font-medium uppercase tracking-[0.24em] text-[#7fe8ff]">Step {i + 1}</span>
                        <h3 className="mt-1 font-title text-lg font-bold tracking-tight">{s.title}</h3>
                        <p className="mt-2 font-body text-sm font-light leading-relaxed text-[#c6d6e4]">{s.body}</p>
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CUSTOM DESIGN SOLUTIONS */}
        <section className="px-6 lg:px-16 py-24">
          <div className="mx-auto max-w-6xl">
            <Reveal><h2 className="font-title text-3xl font-bold tracking-tight lg:text-4xl">Custom Design Solutions</h2></Reveal>
            <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {SOLUTIONS.map((s, i) => (
                <Reveal key={s.title} delay={(i % 3) * 0.08}>
                  <div className="group relative h-full overflow-hidden rounded-3xl border border-[#F7FAFC]/12 bg-[#0b1626]/50 p-8 backdrop-blur-2xl transition-all duration-500 hover:-translate-y-1.5 hover:border-[#35e0d1]/45 hover:shadow-[0_20px_60px_-30px_#35e0d1]" data-testid={`design-solution-${i}`}>
                    <div className="flex h-14 w-14 items-center justify-center rounded-xl border border-[#35e0d1]/25 bg-[#35e0d1]/10 text-[#7fe8ff]">
                      <s.icon className="h-7 w-7" strokeWidth={1.5} />
                    </div>
                    <h3 className="mt-6 font-title text-lg font-bold tracking-tight">{s.title}</h3>
                    <p className="mt-3 font-body text-sm font-light leading-relaxed text-[#c6d6e4]">{s.body}</p>
                    <ChevronRight className="mt-5 h-5 w-5 text-[#7fe8ff] opacity-0 transition-all duration-500 group-hover:translate-x-1 group-hover:opacity-100" />
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* PREMIUM ACRYLIC EXPERTISE */}
        <section className="px-6 lg:px-16 py-24">
          <div className="mx-auto max-w-6xl">
            <div className="grid items-center gap-10 lg:grid-cols-2">
              <Reveal>
                <div className="relative aspect-[4/3] overflow-hidden rounded-3xl border border-[#F7FAFC]/12">
                  <img src={ACRYLIC_IMG} alt="Polished acrylic aquarium construction" className="h-full w-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#03101d]/60 via-transparent to-transparent" />
                </div>
              </Reveal>
              <Reveal delay={0.1}>
                <span className="font-body text-xs font-medium uppercase tracking-[0.28em] text-[#7fe8ff]">Premium Acrylic Expertise</span>
                <h2 className="mt-3 font-title text-3xl font-bold tracking-tight lg:text-4xl">Crystal-Clear Acrylic Aquariums</h2>
                <p className="mt-5 font-body text-sm font-light leading-relaxed text-[#c6d6e4] lg:text-base">
                  Seatech specializes in designing and installing premium acrylic aquarium systems with
                  exceptional clarity and durability. Acrylic offers incredible viewing clarity, superior
                  strength, and remarkable flexibility—enabling seamless, custom shapes and expansive
                  panoramic displays that glass simply cannot match.
                </p>
              </Reveal>
            </div>

            {/* Acrylic scratch removal */}
            <div className="mt-16 grid items-center gap-10 lg:grid-cols-2">
              <Reveal className="lg:order-2">
                <span className="font-body text-xs font-medium uppercase tracking-[0.28em] text-[#7fe8ff]">Restoration Service</span>
                <h3 className="mt-3 font-title text-2xl font-bold tracking-tight lg:text-3xl">Acrylic Scratch Removal</h3>
                <p className="mt-5 font-body text-sm font-light leading-relaxed text-[#c6d6e4] lg:text-base">
                  Restore your acrylic aquarium to exceptional visual clarity—no replacement required. Our
                  professional buffing and polishing process removes surface scratches and hazing, bringing
                  back that showroom-clear finish. Drag the slider to see the difference.
                </p>
              </Reveal>
              <Reveal delay={0.1} className="lg:order-1">
                <BeforeAfter />
              </Reveal>
            </div>
          </div>
        </section>

        {/* THE WHITE-GLOVE EXPERIENCE */}
        <section className="px-6 lg:px-16 py-24">
          <div className="mx-auto max-w-5xl">
            <Reveal><h2 className="text-center font-title text-3xl font-bold tracking-tight lg:text-4xl">The White-Glove Experience</h2></Reveal>
            <div className="mt-14 space-y-6">
              {WHITEGLOVE.map((w, i) => (
                <Reveal key={w.title} delay={i * 0.08}>
                  <div className={`flex flex-col items-center gap-6 rounded-3xl border border-[#F7FAFC]/12 bg-[#0b1626]/45 p-8 backdrop-blur-2xl transition-all duration-500 hover:border-[#35e0d1]/40 sm:flex-row ${i % 2 === 1 ? "sm:flex-row-reverse sm:text-right" : ""}`} data-testid={`design-whiteglove-${i}`}>
                    <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl border border-[#35e0d1]/30 bg-[#35e0d1]/10 font-title text-xl font-bold text-[#7fe8ff]">0{i + 1}</div>
                    <div>
                      <h3 className="font-title text-xl font-bold tracking-tight">{w.title}</h3>
                      <p className="mt-2 font-body text-sm font-light leading-relaxed text-[#c6d6e4] lg:text-base">{w.body}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* FEATURED INSTALLATION GALLERY */}
        <section id="portfolio" className="px-6 lg:px-16 py-24">
          <div className="mx-auto max-w-6xl">
            <Reveal><h2 className="font-title text-3xl font-bold tracking-tight lg:text-5xl">Featured Installation Gallery</h2></Reveal>
            <div className="mt-12 columns-1 gap-5 sm:columns-2 lg:columns-3">
              {GALLERY.map((src, i) => (
                <Reveal key={i} delay={(i % 3) * 0.1} className="mb-5 break-inside-avoid">
                  <div className="group relative overflow-hidden rounded-3xl border border-[#F7FAFC]/12" data-testid={`design-gallery-${i}`}>
                    <img src={src} alt="Custom aquarium installation" loading="lazy" className="w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-110" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#03101d]/70 via-transparent to-transparent" />
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* WHY INVEST */}
        <section className="px-6 lg:px-16 py-24">
          <div className="mx-auto max-w-6xl">
            <Reveal><h2 className="font-title text-3xl font-bold tracking-tight lg:text-4xl">Why Invest In Professional Installation?</h2></Reveal>
            <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {INVEST.map((c, i) => (
                <Reveal key={c.title} delay={i * 0.1}>
                  <div className="group h-full rounded-3xl border border-[#F7FAFC]/12 bg-[#0b1626]/50 p-7 backdrop-blur-2xl transition-all duration-500 hover:-translate-y-1.5 hover:border-[#35e0d1]/45 hover:shadow-[0_20px_60px_-30px_#35e0d1]" data-testid={`design-invest-${i}`}>
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-[#35e0d1]/25 bg-[#35e0d1]/10 text-[#7fe8ff]">
                      <c.icon className="h-6 w-6" strokeWidth={1.5} />
                    </div>
                    <h3 className="mt-5 font-title text-lg font-bold leading-snug tracking-tight">{c.title}</h3>
                    <p className="mt-3 font-body text-sm font-light leading-relaxed text-[#c6d6e4]">{c.body}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="relative px-6 lg:px-16 py-32">
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#020a16]/82 to-[#020a16]/95" />
          <div className="relative mx-auto max-w-3xl text-center">
            <Reveal>
              <h2 className="font-title text-3xl font-bold tracking-tight lg:text-5xl" data-testid="design-final-heading">Let's Design Your Dream Aquarium</h2>
              <p className="mx-auto mt-6 max-w-2xl font-body text-sm font-light leading-relaxed text-[#c6d6e4] lg:text-base">
                Whether you're envisioning a striking residential centerpiece or a large-scale commercial
                installation, Seatech Aquariums combines innovative design, expert craftsmanship, and
                white-glove installation to bring your vision to life with unmatched attention to detail.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
                <button onClick={() => goHome("bottom")} data-testid="design-final-cta" className="inline-flex items-center gap-3 rounded-full bg-[#35e0d1] px-9 py-4 font-body text-sm font-semibold uppercase tracking-[0.16em] text-[#04221f] transition-all duration-300 hover:shadow-[0_0_44px_-8px_#35e0d1]">Book Your Free Consultation</button>
                <button onClick={() => navigate("/maintenance")} data-testid="design-final-secondary" className="inline-flex items-center gap-2 rounded-full border border-[#F7FAFC]/25 px-8 py-4 font-body text-sm font-light uppercase tracking-[0.16em] text-[#F7FAFC]/90 backdrop-blur-md transition-all duration-300 hover:border-[#F7FAFC]/60">Explore Maintenance Services</button>
              </div>
            </Reveal>
          </div>
        </section>
      </div>
    </main>
  );
}
